import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import RequireGlobalPermissionPage from 'components/permissions/RequireGlobalPermissionPage';
import PreDefinedDataForm, { PreDefinedDataFormData } from 'components/forms/pre-defined-data/PreDefinedDataForm';
import { PREDEFINED_DATA_REDIRECT_AFTER_SAVE } from 'env';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { GLOBAL_PREDEFINED_DATA_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Page used to edit global pre-defined data.
 */
export default function EditGlobalPredefinedDataPage() {
  return (
    <RequireGlobalPermissionPage permission="manage-global-predefined-data">
      <EditGlobalPredefinedDataPageContent />
    </RequireGlobalPermissionPage>
  );
}

/**
 * Page content used to edit global pre-defined data.
 */
function EditGlobalPredefinedDataPageContent() {
  const navigate = useNavigate();
  const { predefinedDataId } = useParams();
  const { getAction } = useApi();

  const { data, loading, error } = useImmediateAction(() => {
    const preDefinedDataGetAction = getAction('PredefinedDataGetGlobal');
    return preDefinedDataGetAction({ parameters: { globalPredefinedDataId: predefinedDataId! } });
  });

  if (error) {
    panic(error);
    return <></>;
  }

  if (!predefinedDataId) {
    return <Navigate to={GLOBAL_PREDEFINED_DATA_PAGE_PATH.original} />;
  }

  /**
   * Handles the form submission.
   */
  function onSubmit({ predefinedDataName, predefinedDataDescription, predefinedDataTypeId }: PreDefinedDataFormData) {
    const preDefinedDataUpdateAction = getAction('PredefinedDataUpdateGlobal');

    return preDefinedDataUpdateAction({
      payload: {
        predefinedDataName,
        predefinedDataDescription: predefinedDataDescription ?? undefined,
      },
      parameters: {
        globalPredefinedDataId: predefinedDataId!,
      },
    })
      .then(() => {
        notifications.show({
          title: 'Globálny predvolený údaj bol upravený',
          message: `Globálny predvolený údaj ${predefinedDataName} bol úspešne upravený.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        if (PREDEFINED_DATA_REDIRECT_AFTER_SAVE === 'list') {
          navigate(GLOBAL_PREDEFINED_DATA_PAGE_PATH.original);
        }
      })
      .catch(panic);
  }

  return (
    <DashboardLayout
      title="Úprava globálneho predvoleného údaju"
      breadcrumbs={[
        { title: 'Nastavenia' },
        { title: 'Globálne predvolené údaje', link: GLOBAL_PREDEFINED_DATA_PAGE_PATH.original },
        { title: 'Úprava globálneho predvoleného údaju' },
      ]}
    >
      {!loading && (
        <PreDefinedDataForm
          initialValues={{
            predefinedDataName: data?.key ?? '',
            deviceTypeId: String(data?.deviceType.deviceTypeId) ?? '',
            predefinedDataDescription: data?.value ?? '',
            predefinedDataTypeId: String(data?.predefinedDataType.predefinedDataTypeId) ?? '',
            isGlobal: true,
            isPersonal: false,
          }}
          onSubmit={onSubmit}
          readOnly={{
            predefinedDataTypeId: true,
            deviceTypeId: true,
          }}
        />
      )}
    </DashboardLayout>
  );
}
